/* What a spot is: the shape the app works with, the shape a `spots` row stores, the schemas
   that guard the jsonb columns, and the stable key that names a spot across collections. */

import { z } from "./zod";
import { FLAT, HAND_AT, ORDER, combosOf, r1, r3, type Action } from "./hands";

/** What hero does with one hand: action -> frequency 0..1, adding up to 1. */
export type Cell = Partial<Record<Action, number>>;
export type Cells = Record<string, Cell>;

/** How often hero holds a hand here, for the hands held less than always. Sparse. */
export type Presence = Record<string, number>;

/** What each bet on the table costs, in blinds, as far as the source states it. */
export interface Bets {
  raise?: number;
  "3bet"?: number;
  "4bet"?: number;
  "5bet"?: number;
}

export interface ActionStat {
  combos: number;
  pct: number;
}

/** Where a chart sits in the tree. Every field is a rung of the picker. */
export interface SpotPlace {
  stakes: string;
  gameType: string;
  format: string;
  stack: string;
  openSize: string;
  hero: string;
  action: string;
  /** The opponents the action names, in the order the action names them. */
  seats: string[];
  /** The answer an opponent gave the last raise before hero saw it. */
  branch?: string | undefined;
  /** The opponent profile this chart is written against, by id. Absent on the chart that is
      against nobody in particular — which is every chart written before variations. */
  profile?: string | undefined;
}

/** The chart itself: enough for sampling, grading and presence. */
export interface Charted {
  cells: Cells;
  range?: Presence | undefined;
  /** The hands hero holds here, in grid order. Defaults to the keys of `cells`. */
  baseInRange?: string[] | undefined;
}

export interface Spot extends SpotPlace, Charted {
  /** Row id in `spots`. Absent on a spot that has not been saved. */
  id?: string | undefined;
  /** Stable slug, unique inside a collection and carried across copies. */
  key: string;
  actions: Action[];
  baseInRange: string[];
  seatLabels: string[];
  bets?: Bets | undefined;
  /** False when hero's range was borrowed from another chart rather than stated. */
  exact?: boolean | undefined;
  totalCombos: number;
  stats: Partial<Record<Action, ActionStat>>;
  copiedFrom?: string | null | undefined;
  updatedAt?: string | undefined;
}

/* ---- the key ---- */

const slug = (part: string): string =>
  part
    .toLowerCase()
    // A dot stays where it is a decimal point ("2.5x") and goes where it is an abbreviation.
    .replace(/\.(?!\d)/g, "")
    // A "+" is no longer kept. From the day variations arrived it belongs to the profile
    // suffix alone, so a variation's key can never be another spot's whatever the profile is
    // called. No key in the database or in its export held one.
    .replace(/[^a-z0-9.]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * The stable name of a spot: every rung of the tree, slugged and joined, as in
 * `nl10-25-cash-games-6max-100bb-2.5x-btn-vs-3-bet-bb`. Two charts that answer the same
 * question share a key whichever collection they live in, which is what lets a copy be
 * compared with its origin and stats be grouped across users.
 *
 * A variation is that same question against one kind of opponent, so its profile sits at the
 * very end, behind the one "+" a key can hold: `…-btn-rfi` is the chart,
 * `…-btn-rfi+nit` its variation against a NIT. Everything keyed on a spot key then tells the
 * two apart without being taught — the stats row, the leak finder, the global comparison.
 */
export function spotKey(p: SpotPlace): string {
  const place = [
    p.stakes,
    p.gameType,
    p.format,
    p.stack,
    p.openSize,
    p.hero,
    p.action,
    ...p.seats,
    ...(p.branch ? [p.branch] : []),
  ]
    .map(slug)
    .join("-");
  return p.profile ? `${place}+${p.profile}` : place;
}

/** The same place as plain fields, for building the key of a neighbouring chart. */
export const placeOf = (s: SpotPlace): SpotPlace => ({
  stakes: s.stakes,
  gameType: s.gameType,
  format: s.format,
  stack: s.stack,
  openSize: s.openSize,
  hero: s.hero,
  action: s.action,
  seats: [...s.seats],
  ...(s.branch ? { branch: s.branch } : {}),
  ...(s.profile ? { profile: s.profile } : {}),
});

/** Spots by key, the lookup every cross-chart reading goes through. */
export type SpotIndex = ReadonlyMap<string, Spot>;
export const indexSpots = (spots: readonly Spot[]): Map<string, Spot> =>
  new Map(spots.map((s) => [s.key, s]));

/* ---- schemas: the client is untrusted, so every jsonb payload is parsed before a write ---- */

const hand = z.string().refine((h) => HAND_AT[h] !== undefined, "not a hand class");
const frequency = z.number().min(0).max(1);
const label = z.string().min(1).max(40);

export const actionSchema = z.enum(ORDER);
export const cellSchema = z.partialRecord(actionSchema, frequency);
export const cellsSchema = z
  .record(hand, cellSchema)
  .refine((c) => Object.keys(c).length > 0, "a chart holds at least one hand");
export const betsSchema = z.partialRecord(
  z.enum(["raise", "3bet", "4bet", "5bet"]),
  z.number().positive().max(10000),
);

export const spotMetaSchema = z.strictObject({
  stakes: label,
  gameType: label,
  format: label,
  stack: label,
  openSize: label,
  hero: label,
  action: label,
  seats: z.array(label).max(2),
  branch: label.optional(),
  // The profile's id, shaped as `opponents.ts` shapes it: what a key may hold after its "+".
  profile: z
    .string()
    .max(40)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "not a profile id")
    .optional(),
  range: z.record(hand, frequency).optional(),
  bets: betsSchema.optional(),
  exact: z.boolean().optional(),
});
export type SpotMeta = z.infer<typeof spotMetaSchema>;

/** One row of `spots`, as PostgREST returns it and as the importer writes it. */
export const spotRowSchema = z.object({
  id: z.uuid().optional(),
  collection_id: z.uuid().optional(),
  key: z.string().regex(/^[a-z0-9][a-z0-9.+-]{0,199}$/),
  meta: spotMetaSchema,
  actions: z.array(actionSchema).max(4),
  cells: cellsSchema,
  copied_from: z.uuid().nullable().optional(),
  updated_at: z.string().optional(),
});
export type SpotRow = z.infer<typeof spotRowSchema>;

/* ---- between the two shapes ---- */

/** What each opponent slot of an action is called, by action. */
export const ROLE_OF: Readonly<Record<string, readonly string[]>> = {
  "vs. RFI": ["Opponent Seat"],
  "vs. 3 Bet": ["Opponent Seat"],
  "vs. 4 Bet": ["Opponent Seat"],
  "vs. 5 Bet": ["Opponent Seat"],
  "Cold 4bet": ["Raiser Seat", "3-Bettor Seat"],
  "vs. Raise-Call": ["Raiser Seat", "Caller Seat"],
  "vs. Squeeze": ["Caller Seat", "Squeezer Seat"],
  "Called vs. Squeeze": ["Raiser Seat", "Squeezer Seat"],
};

/** What each opponent slot of an action is called. */
export const seatLabelsFor = (action: string, seats: readonly string[]): string[] =>
  seats.map((_, i) => ROLE_OF[action]?.[i] ?? "Opponent Seat");

/** How often hero holds one hand here. 1 unless the spot says otherwise. */
export const presenceAt = (spot: Charted, h: string): number => spot.range?.[h] ?? 1;

/**
 * The totals a chart shows under the grid, counted from its cells: combos hero holds, and
 * how many of them go to each action. Derived, never stored, so it cannot drift.
 */
export function summarize(spot: Charted): Pick<Spot, "actions" | "totalCombos" | "stats"> {
  const totals: Partial<Record<Action, number>> = {};
  let tot = 0;
  for (const h of FLAT) {
    const d = spot.cells[h];
    if (!d) continue;
    const p = presenceAt(spot, h);
    if (p <= 0) continue;
    const n = combosOf(h) * p;
    tot += n;
    for (const a of ORDER) {
      const w = d[a];
      if (w) totals[a] = (totals[a] ?? 0) + w * n;
    }
  }
  const actions = ORDER.filter((a) => (totals[a] ?? 0) > 0);
  return {
    actions,
    totalCombos: r1(tot),
    stats: Object.fromEntries(
      actions.map((a) => {
        const c = totals[a] ?? 0;
        return [a, { combos: r1(c), pct: tot ? r1((100 * c) / tot) : 0 }];
      }),
    ),
  };
}

/** A stored row as the spot the app works with. */
export function spotFromRow(row: SpotRow): Spot {
  const { range, bets, exact, branch, profile, ...place } = row.meta;
  const cells = row.cells as Cells;
  return {
    id: row.id,
    key: row.key,
    ...place,
    ...(branch ? { branch } : {}),
    ...(profile ? { profile } : {}),
    cells,
    ...(range ? { range } : {}),
    ...(bets ? { bets } : {}),
    exact: exact ?? true,
    baseInRange: FLAT.filter((h) => cells[h]),
    seatLabels: seatLabelsFor(place.action, place.seats),
    ...summarize({ cells, range }),
    copiedFrom: row.copied_from ?? null,
    updatedAt: row.updated_at,
  };
}

/** A spot as the row that stores it. Parsed on the way out: a bad shape throws here. */
export function rowFromSpot(spot: Spot): SpotRow {
  const range: Presence = {};
  for (const [h, p] of Object.entries(spot.range ?? {})) if (r3(p) < 1) range[h] = r3(p);
  return spotRowSchema.parse({
    ...(spot.id ? { id: spot.id } : {}),
    key: spot.key,
    meta: {
      ...placeOf(spot),
      ...(Object.keys(range).length ? { range } : {}),
      ...(spot.bets && Object.keys(spot.bets).length ? { bets: spot.bets } : {}),
      ...(spot.exact === false ? { exact: false } : {}),
    },
    actions: ORDER.filter((a) => Object.values(spot.cells).some((d) => (d[a] ?? 0) > 0)),
    cells: spot.cells,
    ...(spot.copiedFrom ? { copied_from: spot.copiedFrom } : {}),
  });
}
